import type { BillingPaymentTerm, BillingTier } from '@zeus/index'
import { $, Selector } from '@zeus/index'
import { typedGql } from '@zeus/typedDocumentNode'
import { scalars } from '~/gql/utils/scalars'

const countersSelector = Selector('LearnerCountersModel')({
  orgId: true,
  orgName: true,
  orgSlug: true,
  month: true,
  monthlyActive: true,
  learnerCount: true,
  registeredLearners: true,
  leavingWithinThreeMonths: true,
  subscribedTier: true,
  effectiveTier: true,
  billedTier: true,
  placesRemaining: true,
  nextTier: true,
  paymentTerm: true,
  estimatedRevenueCents: true,
})

/** Compteurs de toutes les organisations (SuperAdmin). */
export const GQL_LEARNER_COUNTERS_BY_ORGANIZATION = typedGql('query')({
  learnerCountersByOrganization: [{ month: $('month', 'String') }, countersSelector],
})

/** Compteurs de l'organisation de l'admin connecté. */
export const GQL_LEARNER_COUNTERS = typedGql('query')({
  learnerCounters: [{ month: $('month', 'String') }, countersSelector],
})

const accountFields = {
  id: true,
  tier: true,
  paymentTerm: true,
  tierChangedAt: true,
  companyName: true,
  addressLine1: true,
  addressLine2: true,
  postalCode: true,
  city: true,
  country: true,
  vatNumber: true,
  billingEmail: true,
} as const

/** Palier et coordonnées de facturation de l'organisation (Admin). */
export const GQL_BILLING_ACCOUNT = typedGql('query', { scalars })({
  billingAccount: accountFields,
})

/** Ce que le palier coûte sur le mois observé. */
export const GQL_BILLING_ESTIMATE = typedGql('query')({
  billingEstimate: [{ month: $('month', 'String') }, {
    month: true,
    subscribedTier: true,
    effectiveTier: true,
    billedTier: true,
    paymentTerm: true,
    learnerCount: true,
    monthlyActive: true,
    registeredLearners: true,
    placesRemaining: true,
    nextTier: true,
    leavingWithinThreeMonths: true,
    monthlyPriceCents: true,
    monthlyAmountCents: true,
    upfrontYearlyAmountCents: true,
    isCustom: true,
  }],
})

/** Historique mensuel des actifs, du compteur et des montants. */
export const GQL_BILLING_HISTORY = typedGql('query')({
  billingHistory: [{ months: $('months', 'Int') }, {
    month: true,
    monthlyActive: true,
    learnerCount: true,
    tier: true,
    amountCents: true,
  }],
})

/**
 * Ce que la formule ouvre (branding, SSO). Consommé par les écrans back-office
 * pour griser ce qui est verrouillé sur le gratuit (#155) — le refus, lui, reste
 * appliqué côté API.
 */
export const GQL_PLAN_CAPABILITIES = typedGql('query')({
  planCapabilities: {
    tier: true,
    canCustomizeBranding: true,
    canUseSso: true,
  },
})

export const GQL_UPDATE_BILLING_PROFILE = typedGql('mutation', { scalars })({
  updateBillingProfile: [{ input: $('input', 'UpdateBillingProfileInput!') }, accountFields],
})

export const GQL_CHANGE_BILLING_PLAN = typedGql('mutation', { scalars })({
  changeBillingPlan: [{ input: $('input', 'ChangeBillingPlanInput!') }, accountFields],
})

// Types concrets pour le rendu : les `InputType` zeus résolvent leurs champs en
// `unknown` (inexploitables en accès de champ), d'où ces formes explicites.
export type LearnerCountersRow = {
  orgId: string
  orgName: string
  orgSlug: string
  month: string
  monthlyActive: number
  learnerCount: number
  registeredLearners: number
  leavingWithinThreeMonths: number
  subscribedTier: BillingTier
  effectiveTier: BillingTier
  billedTier: BillingTier
  placesRemaining?: number | null
  nextTier?: BillingTier | null
  paymentTerm: BillingPaymentTerm
  estimatedRevenueCents: number
}

export type BillingAccount = {
  id: string
  tier: BillingTier
  paymentTerm: BillingPaymentTerm
  tierChangedAt?: Date | null
  companyName?: string | null
  addressLine1?: string | null
  addressLine2?: string | null
  postalCode?: string | null
  city?: string | null
  country?: string | null
  vatNumber?: string | null
  billingEmail?: string | null
}

export type BillingEstimate = {
  month: string
  subscribedTier: BillingTier
  effectiveTier: BillingTier
  billedTier: BillingTier
  paymentTerm: BillingPaymentTerm
  learnerCount: number
  monthlyActive: number
  registeredLearners: number
  placesRemaining?: number | null
  nextTier?: BillingTier | null
  leavingWithinThreeMonths: number
  monthlyPriceCents?: number | null
  monthlyAmountCents?: number | null
  upfrontYearlyAmountCents?: number | null
  isCustom: boolean
}

export type BillingHistoryEntry = {
  month: string
  monthlyActive: number
  learnerCount: number
  tier: BillingTier
  amountCents: number
}

export type PlanCapabilities = {
  tier: BillingTier
  canCustomizeBranding: boolean
  canUseSso: boolean
}
